import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Loader2, Printer } from "lucide-react";
import { PageHeader } from "@/components/layout/PageShell";
import { DataTable } from "@/components/layout/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useReportBranch } from "@/contexts/ReportBranchContext";
import OrderTypeFilter from "@/components/reports/OrderTypeFilter";
import api from "@/lib/api";
import { aed } from "@/lib/format";
import { cancelPreparedBillPrint, prepareBillPrintWindow, printBillReceipt } from "@/lib/billPrint";
import { toast } from "sonner";

function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}

function formatPaidAt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function PosBillHistory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { branchId: reportBranchId } = useReportBranch();
  const branchId = reportBranchId ?? user?.default_branch?.id;
  const [dateFrom, setDateFrom] = useState(todayYmd);
  const [dateTo, setDateTo] = useState(todayYmd);
  const [search, setSearch] = useState("");
  const [orderType, setOrderType] = useState("all");

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["pos-bill-history", branchId, dateFrom, dateTo, search, orderType],
    queryFn: async () => {
      const { data: res } = await api.get("/bills/history", {
        params: {
          branch_id: branchId,
          date_from: dateFrom,
          date_to: dateTo,
          search: search || undefined,
          order_type: orderType !== "all" ? orderType : undefined,
          limit: 100,
        },
      });
      return res;
    },
    enabled: Boolean(branchId && dateFrom && dateTo),
  });

  const reprintMutation = useMutation({
    mutationFn: async (billId) => {
      const { data: res } = await api.post(
        `/bills/${billId}/reprint`,
        {},
        { params: { branch_id: branchId } },
      );
      return res;
    },
    onSuccess: (res) => {
      if (res.print_receipt && printBillReceipt(res.print_receipt)) {
        toast.success(res.print_receipt.dispatch_message || `Reprint ${res.bill.bill_number}`);
      } else {
        cancelPreparedBillPrint();
        toast.error("Reprint data ready but the print dialog did not open.");
      }
    },
    onError: (err) => {
      cancelPreparedBillPrint();
      toast.error(err.response?.data?.message || err.response?.data?.errors?.bill?.[0] || "Reprint failed");
    },
  });

  const bills = data?.bills ?? [];

  const columns = [
    {
      key: "bill_number",
      label: "Bill #",
      render: (value) => <span className="font-mono font-bold text-blue-700">{value}</span>,
    },
    {
      key: "order_type_label",
      label: "Type",
      render: (value, row) => value || row.order_type || "—",
    },
    { key: "customer_name", label: "Customer", bold: true },
    {
      key: "customer_tax_number",
      label: "TRN",
      render: (value) => (
        <span className="font-mono text-[12px] text-slate-700">{value || "—"}</span>
      ),
    },
    {
      key: "table_code",
      label: "Table",
      render: (value) => value || "—",
    },
    {
      key: "net_amount",
      label: "Total",
      align: "right",
      render: (value) => <span className="font-mono font-semibold">{aed(value)}</span>,
    },
    {
      key: "paid_at",
      label: "Paid at",
      render: (value) => (
        <span className="text-slate-600 text-[12.5px]">{formatPaidAt(value)}</span>
      ),
    },
  ];

  function reprint(row) {
    prepareBillPrintWindow();
    reprintMutation.mutate(row.id);
  }

  return (
    <div data-testid="pos-bill-history">
      <PageHeader
        title="POS Bill History"
        subtitle="Reprint paid bills from today or past dates"
        actions={
          <Button
            size="sm"
            className="h-9 bg-[#2563EB] hover:bg-blue-700"
            onClick={() => navigate("/pos")}
            data-testid="pos-history-open-pos"
          >
            Open POS
          </Button>
        }
      />

      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div>
          <Label className="text-[11px] text-slate-600 mb-1 block">From</Label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="h-9 w-[160px]"
            data-testid="pos-history-date-from"
          />
        </div>
        <div>
          <Label className="text-[11px] text-slate-600 mb-1 block">To</Label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="h-9 w-[160px]"
            data-testid="pos-history-date-to"
          />
        </div>
        <OrderTypeFilter value={orderType} onChange={setOrderType} testId="pos-history-order-type" />
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[11px] text-slate-600 mb-1 block">Search</Label>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Bill number, customer, table…"
            className="h-9"
            data-testid="pos-history-search"
          />
        </div>
        {isFetching && !isLoading ? (
          <Loader2 size={16} className="animate-spin text-slate-400 mb-2" />
        ) : null}
      </div>

      {isLoading ? (
        <div className="h-32 grid place-items-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={bills}
          emptyText="No paid bills in this date range"
          testIdPrefix="pos-history"
          onRowClick={reprint}
          rowActions={(row) => [
            {
              title: "Reprint",
              icon: reprintMutation.isPending
                ? <Loader2 size={14} className="animate-spin" />
                : <Printer size={14} />,
              onClick: () => reprint(row),
            },
          ]}
        />
      )}
    </div>
  );
}
